import type { ButtonHoverThemeColor, ButtonThemeColor } from '@tmh-website/ui/Button';
import type { FormElementThemeColor } from '@tmh-website/ui/FormElements';
import { Color, customColorToCategory } from '@tmh-website/shared/colors';

import type { AudienceTabThemeColor } from './AudienceTab/AudienceTab.theme';
import type { LocaleSwitchBannerThemeColor } from './LocaleSwitchBanner';

export type HeaderThemeColor =
  | Color.Blue_1000_Deep
  | Color.Neutral_0_White
  | Color.Neutral_1000_Black
  | Color.Neutral_200_OffWhite
  | Color.Purple_1000_Technology;

export interface HeaderTheme {
  background: Color;
  text: Color;
  buttonThemeColor: ButtonThemeColor;
  buttonHoverThemeColor: ButtonHoverThemeColor;
  selectThemeColor: FormElementThemeColor;
  localeSwitchBanner: LocaleSwitchBannerThemeColor;
  audienceTabsBackground: Color;
  audienceTabThemeColor: AudienceTabThemeColor;
  subnav: {
    background: Color;
    text: Color;
  };
  outlineButtonThemeColor: ButtonThemeColor;
}

// TODO: Define the `Color.Neutral_1000_Black` theme variant.
export const headerThemes: { [key in HeaderThemeColor]: HeaderTheme } = {
  [Color.Blue_1000_Deep]: {
    background: Color.Blue_1000_Deep,
    text: Color.Neutral_0_White,
    buttonThemeColor: Color.Blue_500_Ultramarine,
    buttonHoverThemeColor: Color.Neutral_0_White,
    selectThemeColor: Color.Blue_1000_Deep,
    localeSwitchBanner: Color.Blue_900,
    audienceTabsBackground: Color.Blue_900,
    audienceTabThemeColor: Color.Blue_1000_Deep,
    subnav: {
      background: Color.Blue_900,
      text: Color.Neutral_0_White,
    },
    outlineButtonThemeColor: Color.Blue_500_Ultramarine,
  },
  [Color.Neutral_0_White]: {
    background: Color.Neutral_0_White,
    text: Color.Blue_1000_Deep,
    buttonThemeColor: Color.Neutral_200_OffWhite,
    buttonHoverThemeColor: Color.Blue_1000_Deep,
    selectThemeColor: Color.Neutral_0_White,
    localeSwitchBanner: Color.Neutral_200_OffWhite,
    audienceTabsBackground: Color.Neutral_200_OffWhite,
    audienceTabThemeColor: Color.Neutral_0_White,
    subnav: {
      background: Color.Blue_1000_Deep,
      text: Color.Neutral_0_White,
    },
    outlineButtonThemeColor: Color.Blue_500_Ultramarine,
  },
  [Color.Neutral_200_OffWhite]: {
    background: Color.Neutral_200_OffWhite,
    text: Color.Blue_1000_Deep,
    buttonThemeColor: Color.Neutral_300,
    buttonHoverThemeColor: Color.Blue_1000_Deep,
    selectThemeColor: Color.Neutral_200_OffWhite,
    localeSwitchBanner: Color.Neutral_0_White,
    audienceTabsBackground: Color.Neutral_0_White,
    audienceTabThemeColor: Color.Neutral_200_OffWhite,
    subnav: {
      background: Color.Blue_1000_Deep,
      text: Color.Neutral_0_White,
    },
    outlineButtonThemeColor: Color.Blue_500_Ultramarine,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   */
  [Color.Neutral_1000_Black]: {
    background: Color.Neutral_1000_Black,
    text: Color.Neutral_0_White,
    buttonThemeColor: Color.Neutral_900,
    buttonHoverThemeColor: Color.Neutral_800,
    selectThemeColor: Color.Neutral_900,
    localeSwitchBanner: Color.Neutral_900,
    audienceTabsBackground: Color.Neutral_900,
    audienceTabThemeColor: Color.Neutral_1000_Black,
    subnav: {
      background: Color.Neutral_900,
      text: Color.Neutral_0_White,
    },
    outlineButtonThemeColor: Color.Neutral_800,
  },

  [Color.Purple_1000_Technology]: {
    background: Color.Purple_1000_Technology,
    text: Color.Neutral_0_White,
    buttonThemeColor: Color.Blue_500_Ultramarine,
    buttonHoverThemeColor: Color.Neutral_0_White,
    selectThemeColor: Color.Purple_1000_Technology,
    localeSwitchBanner: Color.Blue_1000_Deep,
    audienceTabsBackground: Color.Blue_1000_Deep,
    audienceTabThemeColor: Color.Purple_1000_Technology,
    subnav: {
      background: Color.Blue_1000_Deep,
      text: Color.Neutral_0_White,
    },
    outlineButtonThemeColor: Color.Neutral_0_White,
  },
};

/**
 * This constant has been defined only as a weird solution to somehow make the `HeaderThemeColor` type fully compliant
 * with the `customColorToCategory.HeaderThemeColor` color palette. I haven't come up with an idea how it could be done
 * better, more elegant, and robust. If you know how, then feel free to improve this solution :)
 */
// noinspection JSUnusedLocalSymbols
const HEADER_THEME_COLOR_TYPE_GUARD: Record<keyof typeof customColorToCategory.HeaderThemeColor, HeaderThemeColor> = {
  Blue_1000_Deep: Color.Blue_1000_Deep,
  Neutral_0_White: Color.Neutral_0_White,
  Neutral_1000_Black: Color.Neutral_1000_Black,
  Neutral_200_OffWhite: Color.Neutral_200_OffWhite,
  Purple_1000_Technology: Color.Purple_1000_Technology,
};
